import {
  Box,
  Button,
  Center,
  Grid,
  HStack,
  Icon,
  IconButton,
  Text,
  VStack
} from "@chakra-ui/react";
import { FaUser, FaUserTie } from "react-icons/fa";
import { useEffect, useState } from "react";
import { GETLicensePayload } from "../../utils";
import { MASTER_PRICE, VO_PRICE } from "../license-and-billing";
import { useApplicationContext } from "contexts/ApplicationContext";
import { CustomModal } from "components/custom-modal";
import { createModalTrigger, useModalControls } from "hooks/use-modal-controls";
import {
  calculateProportionalValue,
  handleSaveUserLicence,
  parseISODate,
  startOfDay
} from "./utils";
import { CloseIcon } from "components/vectors/close-icon";
import { CheckCircleIcon } from "@chakra-ui/icons";

interface ManageLicensesModalProps {
  licenseData: Partial<GETLicensePayload>;
  onUpdateLicenses: (data?: GETLicensePayload) => void;
}

const MODAL_EVENT_NAME = "openManageLicensesModal";

export const openManageLicensesModal = createModalTrigger(MODAL_EVENT_NAME);

export function ManageLicensesModal({
  licenseData,
  onUpdateLicenses
}: ManageLicensesModalProps) {
  const { isOpen, onClose } = useModalControls(MODAL_EVENT_NAME);
  const { user } = useApplicationContext();

  const customerId = user.userData.customerId!;
  const masterActive = licenseData.qtyEnabledMaster ?? 0;
  const masterContracted = licenseData.pendingQtyLicenseMaster ?? 0;
  const voContracted = licenseData.pendingQtyLicenseVendedorOrOperacional ?? 0;

  const MAX_MASTER = Math.max(
    masterActive,
    licenseData.qtyLicenseMaster ?? 0,
    masterContracted,
    1
  );
  const MAX_VO = 200;

  const [targetMaster, setTargetMaster] = useState(masterContracted);
  const [targetVO, setTargetVO] = useState(voContracted);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setTargetMaster(masterContracted);
    setTargetVO(voContracted);
  }, [isOpen, masterContracted, voContracted]);

  const today = startOfDay(new Date());
  const expirationDate = licenseData.expirationDate
    ? parseISODate(licenseData.expirationDate)
    : today;

  const isMasterChanged = targetMaster !== masterContracted;
  const isVOChanged = targetVO !== voContracted!;
  const hasBillingChange =
    targetMaster > licenseData.qtyLicenseMaster! ||
    targetVO > licenseData.qtyLicenseVendedorOrOperacional!;
  const isExpired = licenseData.expired === true;
  const isCycleExpired = isExpired || expirationDate <= today;
  const shouldShowBillingSummary = hasBillingChange || isExpired;
  const hasChanges = isMasterChanged || isVOChanged;
  const masterDiff = targetMaster - licenseData.qtyLicenseMaster!;
  const voDiff = targetVO - licenseData.qtyLicenseVendedorOrOperacional!;
  const masterProRata =
    masterDiff > 0 && !isCycleExpired
      ? calculateProportionalValue(
          today,
          expirationDate,
          masterDiff * MASTER_PRICE
        )
      : 0;
  const voProRata =
    voDiff > 0 && !isCycleExpired
      ? calculateProportionalValue(today, expirationDate, voDiff * VO_PRICE)
      : 0;
  const totalPayNow =
    (masterDiff > 0 ? masterProRata : 0) + (voDiff > 0 ? voProRata : 0);
  const newMonthlyTotal = targetMaster * MASTER_PRICE + targetVO * VO_PRICE;
  const amountToPayNow = licenseData.userCanRenew
    ? newMonthlyTotal
    : totalPayNow;

  const fmt = (v: number) => `R$ ${v.toFixed(2).replace(".", ",")}`;

  const handleCancel = () => {
    setTargetMaster(masterContracted);
    setTargetVO(voContracted);
    onClose();
  };

  const shouldShowPrimaryAction = hasChanges || isExpired;
  const primaryActionLabel = shouldShowBillingSummary
    ? "Pagar e Salvar Alterações"
    : "Salvar Alterações";

  const modalFooter = (
    <HStack spacing={3}>
      {shouldShowPrimaryAction && (
        <Button
          isDisabled={isLoading}
          isLoading={isLoading}
          onClick={() =>
            handleSaveUserLicence({
              customerId,
              onClose,
              onUpdateLicenses,
              setIsLoading,
              targetMaster,
              targetVO
            })
          }
          leftIcon={<CheckCircleIcon />}
        >
          {primaryActionLabel}
        </Button>
      )}{" "}
      {hasChanges && (
        <Button
          variant="outline"
          leftIcon={<CloseIcon />}
          onClick={handleCancel}
        >
          Cancelar
        </Button>
      )}
    </HStack>
  );

  return (
    <CustomModal
      modalTitle="Gerenciar Licença"
      isOpen={isOpen}
      onClose={handleCancel}
      modalFooter={modalFooter}
      size="lg"
    >
      <VStack spacing={8} align="stretch">
        <HStack justify="space-between">
          <HStack spacing={3}>
            <Center p={2} bg="blue.50" borderRadius="lg">
              <Icon
                as={FaUserTie}
                w={5}
                h={5}
                color="var(--chakra-colors-blue-600)"
              />
            </Center>
            <Box>
              <Text fontWeight="medium" color="gray.900">
                Master
              </Text>
              <Text fontSize="xs" color="gray.500">
                Acesso administrativo completo
              </Text>
              <Text fontSize="xs" fontWeight="medium" color="blue.600" mt={1}>
                {fmt(MASTER_PRICE)}/mês
              </Text>
            </Box>
          </HStack>

          <Grid
            gap="4px"
            alignItems="center"
            gridTemplateColumns="auto 30px auto"
          >
            <IconButton
              aria-label="Reduzir licença master"
              icon={<>-</>}
              color="gray.500"
              variant="ghost"
              onClick={() => setTargetMaster(Math.max(1, targetMaster - 1))}
              isDisabled={targetMaster <= 1}
            />
            <Center
              textAlign="center"
              fontWeight="semibold"
              color="gray.900"
              fontSize="lg"
            >
              {targetMaster}
            </Center>
            <IconButton
              aria-label="Aumentar licença master"
              icon={<>+</>}
              color="gray.500"
              variant="ghost"
              onClick={() =>
                setTargetMaster(Math.min(MAX_MASTER, targetMaster + 1))
              }
              isDisabled={targetMaster >= MAX_MASTER}
            />
          </Grid>
        </HStack>

        <Box
          w="100%"
          borderTop="1px solid var(--chakra-colors-gray-200)"
          my="2"
        />

        <HStack justify="space-between">
          <HStack spacing={3}>
            <Center p={2} bg="blue.50" color="blue.600" borderRadius="lg">
              <Icon as={FaUser} w={5} h={5} />
            </Center>
            <Box>
              <Text fontWeight="medium" color="gray.900">
                Vendedor / Operacional
              </Text>
              <Text fontSize="xs" color="gray.500">
                Acesso padrão ao sistema
              </Text>
              <Text fontSize="xs" fontWeight="medium" color="blue.600" mt={1}>
                {fmt(VO_PRICE)}/mês
              </Text>
            </Box>
          </HStack>

          <Grid
            gap="4px"
            alignItems="center"
            gridTemplateColumns="auto 30px auto"
          >
            <IconButton
              aria-label="Reduzir licença vendedor operacional"
              icon={<>-</>}
              color="gray.500"
              variant="ghost"
              onClick={() => setTargetVO(Math.max(0, targetVO - 1))}
            />
            <Center
              textAlign="center"
              fontWeight="semibold"
              color="gray.900"
              fontSize="lg"
            >
              {targetVO}
            </Center>
            <IconButton
              aria-label="Aumentar licença vendedor operacional"
              icon={<>+</>}
              color="gray.500"
              variant="ghost"
              onClick={() => setTargetVO(Math.min(MAX_VO, targetVO + 1))}
              isDisabled={targetVO >= MAX_VO}
            />
          </Grid>
        </HStack>

        {shouldShowBillingSummary && (
          <VStack spacing={3} align="stretch">
            {amountToPayNow > 0 && (
              <Box
                bg="gray.50"
                borderRadius="lg"
                p={4}
                border="1px"
                borderColor="gray.200"
              >
                <VStack spacing={2} align="stretch">
                  {masterDiff > 0 &&
                    !isExpired &&
                    !licenseData.userCanRenew && (
                      <HStack justify="space-between" fontSize="sm">
                        <Text color="gray.600">Master (+{masterDiff})</Text>
                        <Text color="gray.900" fontWeight="medium">
                          {fmt(masterProRata)}
                        </Text>
                      </HStack>
                    )}

                  {voDiff > 0 && !isExpired && !licenseData.userCanRenew && (
                    <HStack justify="space-between" fontSize="sm">
                      <Text color="gray.600">
                        Vendedor/Operacional (+{voDiff})
                      </Text>
                      <Text color="gray.900" fontWeight="medium">
                        {fmt(voProRata)}
                      </Text>
                    </HStack>
                  )}

                  <Box
                    pt={isExpired || licenseData.userCanRenew ? 0 : 2}
                    borderTop={
                      isExpired || licenseData.userCanRenew ? "0px" : "1px"
                    }
                    borderColor="gray.200"
                  >
                    <HStack justify="space-between" align="center">
                      <Text fontSize="sm" fontWeight="medium" color="gray.900">
                        Total a pagar agora
                      </Text>
                      <Text fontSize="lg" fontWeight="bold" color="red.600">
                        {fmt(amountToPayNow)}
                      </Text>
                    </HStack>
                    <Text fontSize="xs" color="gray.500" mt={1}>
                      {isExpired || licenseData.userCanRenew
                        ? `${licenseData.userCanRenew ? "" : "A licença está expirada. "}O valor acima corresponde à cobrança integral da licença selecionada para o novo ciclo. `
                        : "O valor acima será cobrado proporcionalmente ao período restante do ciclo atual, que termina em "}

                      {!isExpired &&
                        !licenseData.userCanRenew &&
                        (licenseData.expirationDate
                          ? licenseData.expirationDate.replace(
                              /(\d{4})-(\d{2})-(\d{2})/,
                              "$3/$2/$1"
                            )
                          : "data desconhecida")}

                      {!isExpired &&
                        !licenseData.userCanRenew &&
                        ". Na próxima renovação, será cobrado o valor integral pela licença contratada."}
                    </Text>
                  </Box>
                </VStack>
              </Box>
            )}
          </VStack>
        )}
      </VStack>
    </CustomModal>
  );
}
